import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Flag, LocateFixed, MapPin, Route as RouteIcon, Zap } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { driver, useStations } from '@/api/driver'
import type { Route } from '@/api/proposed'
import { PageTitle, StatusPill } from '@/components/common'
import { StationsMap } from '@/components/map'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, Input, Select } from '@/components/ui/form'
import { useAuth } from '@/lib/auth'
import { kw } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { PLACES } from '@/lib/places'
import { getRoute, planStops, stationsAlong, type Plan } from '@/lib/route'
import { cn, errorText } from '@/lib/utils'
import { freeConnectors } from './MapPage'

const ME = '__me__'
const hm = (min: number) => {
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  return h ? `${h} h ${m} min` : `${m} min`
}

export function RoutePage() {
  const { t } = useI18n()
  const { user } = useAuth()
  const stations = useStations()
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: driver.vehicles, enabled: !!user })
  const primary = vehicles.data?.vehicles.find((v) => v.is_primary) ?? vehicles.data?.vehicles[0]
  const [from, setFrom] = useState('Kathmandu')
  const [to, setTo] = useState('Pokhara')
  const [me, setMe] = useState<{ lat: number; lng: number } | null>(null)
  const [battery, setBattery] = useState<string>('')
  const [efficiency, setEfficiency] = useState('6.5')
  const [startPct, setStartPct] = useState('90')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ route: Route; plan: Plan; along: ReturnType<typeof stationsAlong> } | null>(null)

  const batteryKwh = Number(battery || primary?.battery_kwh || 40)
  const rangeKm = batteryKwh * Number(efficiency || 6.5)

  const point = (name: string): [number, number] | null => {
    if (name === ME) return me ? [me.lat, me.lng] : null
    const p = PLACES.find((x) => x.name === name)
    return p ? [p.lat, p.lng] : null
  }
  const locate = () =>
    navigator.geolocation?.getCurrentPosition(
      (p) => { setMe({ lat: p.coords.latitude, lng: p.coords.longitude }); setFrom(ME) },
      () => setError(t('Could not get your location. Choose a town instead.')),
      { timeout: 10_000 },
    )

  const plan = async () => {
    const a = point(from)
    const b = point(to)
    if (!a || !b || from === to) return
    setBusy(true)
    setError(null)
    try {
      const route = await getRoute(a, b)
      const along = stationsAlong(route, stations.data?.stations ?? [])
      const p = planStops(route.distance_km, along, { rangeKm, batteryKwh, startPct: Number(startPct), reservePct: 15, targetPct: 80 })
      setResult({ route, plan: p, along })
    } catch (e) {
      setError(errorText(e))
      setResult(null)
    } finally {
      setBusy(false)
    }
  }

  const stopIds = new Set(result?.plan.stops.map((s) => s.station.id))
  const pins = (result?.along ?? []).map((a) => ({ id: a.station.id, lat: a.station.latitude!, lng: a.station.longitude!, label: stopIds.has(a.station.id) ? '★' : String(freeConnectors(a.station)), off: !stopIds.has(a.station.id) && freeConnectors(a.station) === 0 }))
  const nearStart = result?.along.find((a) => a.along < 5 && a.dc)
  const where = (km: number, total: number) => (km < 2 ? t('Near the start') : total - km < 2 ? t('Near the destination') : t('At {km} km', { km: Math.round(km) }))
  const placeOptions = PLACES.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)

  return (
    <div>
      <PageTitle title={t('Plan a trip')} sub={t('Where to charge on the way, for your car’s range.')} />
      <Card className="grid gap-4 p-4 sm:grid-cols-2">
        <Field label={t('From')}>
          {(p) => (
            <div className="flex gap-2">
              <Select {...p} value={from} onChange={(e) => setFrom(e.target.value)}>
                {me && <option value={ME}>{t('My location')}</option>}
                {placeOptions}
              </Select>
              <Button variant="outline" size="icon" aria-label={t('Use my location')} onClick={locate}><LocateFixed /></Button>
            </div>
          )}
        </Field>
        <Field label={t('To')}>{(p) => <Select {...p} value={to} onChange={(e) => setTo(e.target.value)}>{placeOptions}</Select>}</Field>
        <Field label={t('Battery size (kWh)')} hint={primary ? t('From your {car}', { car: `${primary.brand} ${primary.model}` }) : t('Add your car in Account to fill this in.')}>
          {(p) => <Input {...p} type="number" min={10} max={200} value={battery || primary?.battery_kwh || ''} placeholder="40" onChange={(e) => setBattery(e.target.value)} />}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Charge now (%)')}>{(p) => <Input {...p} type="number" min={5} max={100} value={startPct} onChange={(e) => setStartPct(e.target.value)} />}</Field>
          <Field label={t('km per kWh')} hint={t('Hills use more')}>{(p) => <Input {...p} type="number" min={2} max={12} step="0.5" value={efficiency} onChange={(e) => setEfficiency(e.target.value)} />}</Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
          <p className="text-[13px] text-text-2">{t('Range about {km} km on a full battery.', { km: Math.round(rangeKm) })}</p>
          <Button variant="brand" loading={busy} disabled={from === to || !point(from) || !point(to)} onClick={plan}><RouteIcon /> {t('Plan route')}</Button>
        </div>
      </Card>
      {error && <p role="alert" className="mt-3 rounded-lg bg-danger-soft p-3 text-sm text-danger">{error}</p>}

      {result && (
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Card className="p-3"><p className="text-xs text-text-2">{t('Distance')}</p><p className="tabular text-lg font-semibold">{Math.round(result.route.distance_km)} km</p></Card>
            <Card className="p-3"><p className="text-xs text-text-2">{t('Driving')}</p><p className="tabular text-lg font-semibold">{hm(result.route.duration_min)}</p></Card>
            <Card className="p-3"><p className="text-xs text-text-2">{t('Charging stops')}</p><p className="tabular text-lg font-semibold">{result.plan.stops.length}</p></Card>
          </div>
          <StationsMap className="mt-4 h-72 overflow-hidden rounded-xl border border-border sm:h-96" pins={pins} line={result.route.geometry} />

          <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Your trip')}</h2>
          <Card className="divide-y divide-border">
            <Leg icon={<MapPin className="size-4" />} title={from === ME ? t('My location') : from} sub={t('Leave with {p}%', { p: startPct })} />
            {result.plan.stops.map((s) => (
              <Leg
                key={s.station.id}
                icon={<Zap className="size-4 fill-current" />}
                tone="brand"
                title={<Link to={`/stations/${s.station.id}`} className="hover:underline">{s.station.name}</Link>}
                sub={t('At {km} km · arrive with about {a}% · charge to {b}%, roughly {m}', { km: Math.round(s.along), a: Math.max(0, Math.round(s.arrivePct)), b: Math.round(s.leavePct), m: hm(s.minutes) })}
                extra={<span className="flex flex-wrap gap-1.5">{freeConnectors(s.station) ? <StatusPill tone="success">{t('{n} free now', { n: freeConnectors(s.station) })}</StatusPill> : <StatusPill tone="neutral">{t('None free now')}</StatusPill>}<span className="text-xs text-text-2">{s.dc ? t('Fast (DC)') : 'AC'} · {kw(s.maxKw)}</span></span>}
              />
            ))}
            {result.plan.gapAtKm != null && (
              <Leg
                icon={<AlertTriangle className="size-4" />}
                tone="danger"
                title={t('No charger in reach')}
                sub={t('After about {km} km the battery would fall below 15% with no station on the way. Start with more charge or plan a longer stop.', { km: result.plan.gapAtKm })}
                extra={nearStart && <Link to={`/stations/${nearStart.station.id}`} className="text-[13px] font-medium underline">{t('Charge first at {station}, near the start', { station: nearStart.station.name })}</Link>}
              />
            )}
            <Leg icon={<Flag className="size-4" />} title={to} sub={result.plan.gapAtKm == null ? t('Arrive with about {p}%', { p: Math.max(0, Math.round(result.plan.arrivePct)) }) : undefined} />
          </Card>

          {result.along.length > result.plan.stops.length && (
            <>
              <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Other stations on the way')}</h2>
              <Card className="divide-y divide-border">
                {result.along.filter((a) => !stopIds.has(a.station.id)).map((a) => (
                  <Link key={a.station.id} to={`/stations/${a.station.id}`} className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-surface-2">
                    <span className="min-w-0"><span className="block truncate font-medium">{a.station.name}</span><span className="text-xs text-text-2">{where(a.along, result.route.distance_km)}{a.off > 1 ? ` · ${t('{km} km off the road', { km: a.off.toFixed(1) })}` : ''}</span></span>
                    <span className="text-xs text-text-2">{a.dc ? t('Fast (DC)') : 'AC'}</span>
                  </Link>
                ))}
              </Card>
            </>
          )}
          <p className="mt-3 text-xs text-text-2">{t('Estimates only: hills, speed, AC and weather change your range. Routes from OpenStreetMap.')}</p>
        </>
      )}
    </div>
  )
}

function Leg({ icon, title, sub, extra, tone }: { icon: React.ReactNode; title: React.ReactNode; sub?: string; extra?: React.ReactNode; tone?: 'brand' | 'danger' }) {
  return (
    <div className="flex gap-3 px-4 py-3">
      <span className={cn('mt-0.5 grid size-8 shrink-0 place-items-center rounded-full', tone === 'brand' ? 'bg-brand text-ink' : tone === 'danger' ? 'bg-danger-soft text-danger' : 'bg-surface-3 text-text-2')}>{icon}</span>
      <div className="min-w-0 text-sm">
        <p className="font-medium">{title}</p>
        {sub && <p className="text-[13px] text-text-2">{sub}</p>}
        {extra && <div className="mt-1">{extra}</div>}
      </div>
    </div>
  )
}
